import React, {PureComponent, Suspense} from 'react'
import PropTypes from 'prop-types'
import withQuery from 'with-query'
import {compose} from 'redux'
import {connect} from 'react-redux'
import {createStructuredSelector} from 'reselect'
import {AddIcon, QuestionIcon, EditIcon, Tooltip} from 'rambler-ui'
import {retry} from '@rambler-id/promise'
import {reachGoal, trackEvent} from 'utils/metrics'
import {UNBLOCK, TWO_FACTOR_AUTHN} from 'utils/urls'
import {withVerifyingData} from 'models/verifyingData'
import {Link} from 'react-router-dom'
import InternalHeader from 'components/InternalHeader'
import ProfileSection from 'components/ProfileSection'
import ProfileItem from 'components/ProfileItem'
import RowButton from 'components/RowButton'
import EmailIcon from 'components/icons/EmailIcon'
import {SuspensePopup} from 'components/SuspensePopup'
import {showSnackbar} from 'containers/App/actions'
import * as blockStatusActions from 'models/user/blockStatusActions'
import {selectSessionCredentials} from 'models/user/selectors'
import styles from './styles.css'
import {SuspenseFallback} from 'components/SuspenseFallback'
import SberbankIcon from 'components/icons/SberbankIcon'

const DeleteTwoFactorAuthn = React.lazy(
  retry(
    () =>
      import(
        /* webpackChunkName: "add-contact-email" */ 'containers/DeleteTwoFactorAuthn'
      )
  )
)

const GenerateTwoFactorMailPassword = React.lazy(
  retry(
    () =>
      import(
        /* webpackChunkName: "add-contact-email" */ 'containers/GenerateTwoFactorMailPassword'
      )
  )
)

const AddContactEmail = React.lazy(
  retry(
    () =>
      import(
        /* webpackChunkName: "add-contact-email" */ 'containers/AddContactEmail'
      )
  )
)

const ContactEmail = React.lazy(
  retry(
    () =>
      import(
        /* webpackChunkName: "resend-contact-email" */ 'containers/Profile/ContactEmail'
      )
  )
)

class Emails extends PureComponent {
  static propTypes = {
    type: PropTypes.string,
    info: PropTypes.object,
    emails: PropTypes.array,
    blockedRamblerMail: PropTypes.object,
    contactEmails: PropTypes.array,
    sentEmails: PropTypes.array,
    editable: PropTypes.string,
    setEditable: PropTypes.func.isRequired,
    openPopup: PropTypes.func.isRequired,
    deleteContact: PropTypes.func.isRequired,
    resendEmailForContact: PropTypes.func.isRequired,
    emailCredentials: PropTypes.array,
    show2faDeleteSnackbar: PropTypes.func,
    sessionCredentials: PropTypes.object
  }

  get sberbankOauthEmail() {
    const sberSocialProfile = this.props.socialProfiles.find(
      ({provider}) => provider === 'sberbank'
    )

    return (
      sberSocialProfile && (sberSocialProfile.oauthEmail || '').toLowerCase()
    )
  }

  displayProblematicEmail = (accountId, status, extra) => {
    const unblockUrl = withQuery(UNBLOCK, {
      extra,
      email: accountId,
      action: blockStatusActions[status]
    })

    return (
      <span>
        Данный способ входа заблокирован.{' '}
        <a href={unblockUrl} target="_blank" rel="noopener noreferrer">
          Разблокировать
        </a>
      </span>
    )
  }

  deleteTwoFactorAuthn = async (email) => {
    const {sessionCredentials, show2faDeleteSnackbar, openPopup} = this.props
    const {account: sessionAccount, subtype: sessionSubtype} =
      sessionCredentials

    if (
      email !== sessionAccount.replace('#', '@') ||
      sessionSubtype === 'phone'
    ) {
      show2faDeleteSnackbar(email)
      trackEvent('delete_2fa', {need_relogin: true})

      return
    }

    try {
      await openPopup((onResolve, onReject) => (
        <SuspensePopup fallback={<div />}>
          {(props) => (
            <DeleteTwoFactorAuthn
              email={email}
              onResolve={onResolve}
              onReject={onReject}
              {...props}
            />
          )}
        </SuspensePopup>
      )).closed
    } catch (error) {} // eslint-disable-line no-empty
  }

  generateTwoFactorMailPassword = async () => {
    const {info, sessionCredentials, show2faMailSnackbar, openPopup} =
      this.props
    const {account: sessionAccount, subtype: sessionSubtype} =
      sessionCredentials

    if (
      info.email !== sessionAccount.replace('#', '@') ||
      sessionSubtype === 'phone'
    ) {
      show2faMailSnackbar(info.email)
      trackEvent('generate_2fa_mail_password', {need_relogin: true})

      return
    }

    try {
      await openPopup((onResolve, onReject) => (
        <SuspensePopup fallback={<div />}>
          {(props) => (
            <GenerateTwoFactorMailPassword
              onResolve={onResolve}
              onReject={onReject}
              {...props}
            />
          )}
        </SuspensePopup>
      )).closed
    } catch (error) {} // eslint-disable-line no-empty
  }

  track2fa = () => {
    trackEvent('bind_2fa', {email_click: true})
    reachGoal('bind_2fa_email_click')
  }

  render() {
    const {
      type,
      info,
      emails,
      blockedRamblerMail: {
        account: ramblerMailAccount,
        status: ramblerMailStatus
      },
      contactEmails,
      editable,
      setEditable,
      emailCredentials
    } = this.props

    const emailCredentials2fa =
      !!emailCredentials &&
      emailCredentials.reduce(
        (accumulator, credential) => ({
          ...accumulator,
          [credential.account.replace('#', '@')]: credential.is2faEnabled
        }),
        {}
      )

    return (
      <ProfileSection section="emails" color="#4db6ac" icon={<EmailIcon />}>
        <InternalHeader>
          <h2>Адреса электронной почты</h2>
          <p>
            Здесь перечислены все почты, которые вы указывали при использовании
            сервисов Rambler&Co. Список почт также пополняется при привязке
            аккаунтов в социальных сетях. Вы можете добавить любое количество
            дополнительных почт для оперативного информирования вас о критичных
            действиях с вашим профилем.
          </p>
        </InternalHeader>
        {this.sberbankOauthEmail && (
          <ProfileItem muted>
            <div className={styles.row}>
              {this.sberbankOauthEmail}
              <SberbankIcon className={styles.sberIcon} />
            </div>
          </ProfileItem>
        )}
        {type === 'general' && (
          <React.Fragment>
            <ProfileItem
              muted={ramblerMailStatus !== undefined}
              footer={
                ramblerMailStatus &&
                this.displayProblematicEmail(
                  ramblerMailAccount,
                  ramblerMailStatus
                )
              }>
              <a
                className={styles.mail}
                target="_blank"
                rel="noopener noreferrer"
                href="https://mail.rambler.ru">
                {info.email}
              </a>
            </ProfileItem>
            {!ramblerMailStatus && (
              <React.Fragment>
                {!emailCredentials2fa[info.email] ? (
                  <ProfileItem
                    className={styles.twoFactorAuthn}
                    contentClassName={styles.twoFactorAuthnContent}
                    rightIcon={
                      <RowButton
                        container={<Link to={TWO_FACTOR_AUTHN} />}
                        onClick={this.track2fa}
                        data-cerber-id="profile::email::2fa::setup_2fa"
                        label="Настроить двухфакторную аутентификацию">
                        <AddIcon />
                      </RowButton>
                    }>
                    <Link
                      to={TWO_FACTOR_AUTHN}
                      onClick={this.track2fa}
                      data-cerber-id="profile::email::2fa::setup_2fa">
                      Настроить двухфакторную аутентификацию для входа
                    </Link>
                    <Tooltip
                      className={styles.tooltip}
                      contentClassName={styles.tooltipContent}
                      position={'bottom'}
                      content={
                        'Защитите свой профиль не только паролем, но и одноразовыми кодами входа, которые невозможно украсть'
                      }>
                      <QuestionIcon size={15} color="#3764f2" />
                    </Tooltip>
                    <div className={styles.additional}>
                      Надежная защита профиля от взлома
                    </div>
                  </ProfileItem>
                ) : (
                  <ProfileItem
                    className={styles.twoFactorAuthn}
                    contentClassName={styles.twoFactorAuthnContent}
                    rightIcon={
                      <RowButton
                        data-cerber-id="profile::email::2fa::disable_2fa"
                        label="Отключить двухфакторную аутентификацию"
                        onClick={() => this.deleteTwoFactorAuthn(info.email)}>
                        <EditIcon />
                      </RowButton>
                    }>
                    Подключена двухфакторная аутентификация.&nbsp;
                    <button
                      type="button"
                      data-anchor
                      tabIndex="0"
                      data-cerber-id="profile::email::2fa::disable_2fa"
                      onClick={() => this.deleteTwoFactorAuthn(info.email)}>
                      Отключить
                    </button>{' '}
                    <button
                      type="button"
                      data-anchor
                      tabIndex="0"
                      onClick={() => this.generateTwoFactorMailPassword()}
                      data-cerber-id="profile::main::email_client"
                      className={styles.additional}>
                      Я использую почтовый клиент
                    </button>
                  </ProfileItem>
                )}
              </React.Fragment>
            )}
          </React.Fragment>
        )}
        {emails.map(({accountId, status}) => (
          <React.Fragment key={accountId}>
            <ProfileItem
              muted={status !== undefined}
              footer={
                status &&
                this.displayProblematicEmail(accountId, status, 'external')
              }>
              {accountId}
            </ProfileItem>
            {!status && (
              <React.Fragment>
                {!emailCredentials2fa[accountId] ? (
                  <ProfileItem
                    className={styles.twoFactorAuthn}
                    contentClassName={styles.twoFactorAuthnContent}
                    rightIcon={
                      <RowButton
                        container={<Link to={TWO_FACTOR_AUTHN} />}
                        onClick={this.track2fa}
                        label="Настроить двухфакторную аутентификацию"
                        data-cerber-id="profile::email::2fa::setup_2fa">
                        <AddIcon />
                      </RowButton>
                    }
                    cerberId="profile::email::2fa::setup_2fa">
                    <Link
                      to={TWO_FACTOR_AUTHN}
                      onClick={this.track2fa}
                      data-cerber-id="profile::email::2fa::setup_2fa">
                      Настроить двухфакторную аутентификацию для входа
                    </Link>
                    <Tooltip
                      className={styles.tooltip}
                      contentClassName={styles.tooltipContent}
                      position={'bottom'}
                      content={
                        'Защитите свой профиль не только паролем, но и одноразовыми кодами входа, которые невозможно украсть'
                      }>
                      <QuestionIcon size={15} color="#3764f2" />
                    </Tooltip>
                    <div className={styles.additional}>
                      Надежная защита профиля от взлома
                    </div>
                  </ProfileItem>
                ) : (
                  <ProfileItem
                    className={styles.twoFactorAuthn}
                    contentClassName={styles.twoFactorAuthnContent}
                    rightIcon={
                      <RowButton
                        label="Отключить двухфакторную аутентификацию"
                        onClick={() => this.deleteTwoFactorAuthn(accountId)}>
                        <EditIcon />
                      </RowButton>
                    }>
                    Подключена двухфакторная аутентификация.&nbsp;
                    <button
                      type="button"
                      data-anchor
                      tabIndex="0"
                      onClick={() => this.deleteTwoFactorAuthn(accountId)}>
                      Отключить
                    </button>
                  </ProfileItem>
                )}
              </React.Fragment>
            )}
          </React.Fragment>
        ))}
        {contactEmails.map(({contact, type}, index) =>
          contact === this.sberbankOauthEmail ? null : (
            <Suspense key={index} fallback={<div />}>
              <ContactEmail type={type} contact={contact} />
            </Suspense>
          )
        )}
        {editable === 'contactEmail' ? (
          <Suspense fallback={<SuspenseFallback />}>
            <AddContactEmail onClose={() => setEditable(null)} />
          </Suspense>
        ) : (
          <ProfileItem
            rightIcon={
              <RowButton
                label="Добавить почтовый ящик"
                onClick={() => setEditable('contactEmail')}>
                <AddIcon />
              </RowButton>
            }
            cerberId="profile::email::add_email">
            <button
              type="button"
              data-anchor
              tabIndex="0"
              onClick={() => setEditable('contactEmail')}>
              Добавить адрес электронной почты
            </button>
          </ProfileItem>
        )}
      </ProfileSection>
    )
  }
}

const mapStateToProps = createStructuredSelector({
  sessionCredentials: selectSessionCredentials
})

function mapDispatchToProps(dispatch) {
  return {
    show2faDeleteSnackbar: (email) =>
      dispatch(
        showSnackbar(
          `Перед отключением двухфакторной аутентификации выйдите из профиля и войдите снова с помощью почты ${email}`,
          {type: 'main', autoCloseDuration: 10000}
        )
      ),
    show2faMailSnackbar: (email) =>
      dispatch(
        showSnackbar(
          `Чтобы завершить настройку для использования почтового клиента, выйдите из профиля и войдите снова с помощью почты ${email}`,
          {type: 'main', autoCloseDuration: 10000}
        )
      )
  }
}

const withConnect = connect(mapStateToProps, mapDispatchToProps)

export default compose(withVerifyingData(), withConnect)(Emails)
